import { TFunction, Trans } from 'react-i18next';
import { cleanArray, isNullish, theme, SettingsIcon, getFrontendConfigValue } from '@arnold/common';
import styled from '@emotion/styled';
import { FC } from 'react';
import { getTimeStringForOffsetInCzech, parseOffsetInMinutes } from '../../lib/process';
import { AppLink } from '../StyledComponents';
import { SequenceStepsData } from './types';
import { isLoggedUserSysAdmin } from '../../lib/helpers';

export const getColumns = (t: TFunction, topicGroupId: string, isOnboarding?: boolean) =>
  cleanArray([
    {
      key: 'topic',
      title: t('topicName'),
      dataIndex: 'topic' as keyof SequenceStepsData,
    },
    !!isOnboarding && {
      key: 'metrics',
      title: t('onboarding:howDoYouFeel'),
      dataIndex: 'metrics' as keyof SequenceStepsData,
      width: '15%',
    },
    {
      key: 'daysFromStart',
      title: t('SequenceSteps:startOffset'),
      dataIndex: 'daysFromStart' as keyof SequenceStepsData,
      width: '15%',
    },
    {
      key: 'answered',
      title: t('onboarding:responseRate'),
      dataIndex: 'answered' as keyof SequenceStepsData,
    },
    {
      key: 'detail',
      title: (
        <AppLink to={`/stepsEdit/${topicGroupId}`} data-icom={'btn-topic-settings'}>
          <SettingsIcon />
          <StyledSpan>{t('onboarding:stepsSettings')}</StyledSpan>
        </AppLink>
      ),
      dataIndex: 'detail' as keyof SequenceStepsData,
    },
  ]);

const StyledSpan = styled.span`
  margin-left: ${theme.spacing.f};
  font-size: ${theme.typography.body.medium.button.fontSize};
  font-weight: ${theme.typography.body.medium.button.fontWeight};
`;

export const printOffset = (minsOffset: number, start: boolean, t: TFunction) => {
  if (isNullish(minsOffset)) {
    return null;
  }
  const offsetParsed = parseOffsetInMinutes(minsOffset);

  if (isNullish(offsetParsed) || isNullish(offsetParsed.days)) {
    return null;
  }
  return start ? (
    <>
      <Trans
        i18nKey={'processDetail:day'}
        values={{ dayNo: offsetParsed.days + (offsetParsed.days >= 0 ? 1 : 0) }}
        components={[<span />]}
      >
        Day 1
      </Trans>
      , {getTimeStringForOffsetInCzech(offsetParsed)}
    </>
  ) : (
    `${offsetParsed.days >= 0 ? '+' : ''}${offsetParsed.days} ${t('processDetail:days', {
      count: Math.abs(offsetParsed.days),
      postProcess: 'interval',
    })}, ${getTimeStringForOffsetInCzech(offsetParsed)}`
  );
};

const StyledLink = styled(AppLink.withComponent('a'))`
  float: right;
`;

export const TopicDetailLink: FC<{ accessToken: string; topicGroupId: string; label: string }> = ({
  accessToken,
  topicGroupId,
  label,
}) => {
  return (
    <StyledLink
      to=""
      href={`${getFrontendConfigValue('ARNOLD_URL')}/report/${accessToken}?topic_group_id=${topicGroupId}${
        isLoggedUserSysAdmin() ? '&sysAdmin' : ''
      }`}
    >
      {label}
    </StyledLink>
  );
};

export const MetricsInfo: FC<any> = ({ step }: { step: { avgMetric: number } }) => (
  <>{step.avgMetric ? Math.round(step.avgMetric * 10) / 10 : '-'}</>
);

const GraySpan = styled.span`
  color: ${theme.colors.text.secondary};
`;
export const AnsweredSpan: FC<{ answered: number; total?: number }> = ({ answered, total }) => (
  <>
    <span>{`${Math.round(total ? (answered / total) * 100 : 0)}% `}</span>
    <GraySpan>
      ({answered} / {total})
    </GraySpan>
  </>
);
